/**
 * ky-based runtime over the generated Zodios endpoint definitions.
 *
 * Replaces @zodios/core's axios-bound runtime while preserving its alias call
 * semantics exactly (see ZodiosAliases): GETs take one config object
 * (`{ params, queries, headers, signal }`), mutations take the body as the
 * first positional arg. JSON responses are Zod-validated against each
 * endpoint's response schema.
 */
import ky, { isHTTPError, type Options as KyOptions } from 'ky';
import type { ZodiosEndpointDefinition } from '@zodios/core';
import { StoxlyApiError, StoxlyValidationError } from './errors';

export interface StoxlyRefreshOptions {
  /**
   * App-provided refresh (e.g. supabase.auth.refreshSession). Resolve the new
   * access token, or null when the session is gone. Must also update whatever
   * store `getToken` reads from — the retried request re-reads `getToken`.
   */
  refreshToken: () => Promise<string | null>;
  /** Called when refresh yields no token (session truly expired). */
  onSessionExpired?: () => void | Promise<void>;
}

export interface KyRuntimeOptions {
  baseUrl: string;
  getToken?: () => string | null | Promise<string | null>;
  refresh?: StoxlyRefreshOptions;
  kyOptions?: Omit<KyOptions, 'method' | 'prefixUrl'>;
}

interface RequestConfig {
  params?: Record<string, unknown>;
  queries?: Record<string, unknown>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Per-request override of what {@link runtimeTimeout} picks (30s default; see its two families). */
  timeout?: number;
}

const MUTATION_METHODS = new Set(['post', 'put', 'patch', 'delete']);

/**
 * The ONE client timeout for model-backed requests, in ms. The backend aborts its own
 * model call at `LLM_GENERATION_TIMEOUT_MS` (90 s, `apps/backendv2/src/modules/ai/ai.service.ts`);
 * this must stay ABOVE that plus transport margin, or the client gives up on a request
 * the server is still going to persist (the 42 s IR analysis that read as "failed").
 */
export const LLM_REQUEST_TIMEOUT_MS = 120_000;

/**
 * Which generated endpoints run a model: every ai-summary MUTATION (analysis,
 * dividend-safety, the translation family), the portfolio insight generators and the risk
 * narrative (its GET twin is cache-only and stays on the default: MUTATION_METHODS). Detected
 * by path in the runtime so no call site can forget the timeout; the population is pinned
 * by `ky-runtime.spec.ts` so a new model-backed path lands here, not as a 30 s literal.
 */
export function isLlmEndpoint(
  endpoint: Pick<ZodiosEndpointDefinition, 'method' | 'path'>,
): boolean {
  return (
    MUTATION_METHODS.has(endpoint.method) &&
    /^\/api\/(ai-summary\/|portfolios\/:id\/(insight\/generate|risk-analysis\/ai))/.test(
      endpoint.path,
    )
  );
}

/**
 * Which generated endpoints run the BROKER-STATEMENT IMPORTER inline and answer only once the
 * import is finished — S3 upload, xlsx parse, instrument resolution, price backfill, FIFO
 * rebuild. There is no server-side ceiling to sit above the way {@link LLM_REQUEST_TIMEOUT_MS}
 * sits above the model call: the import is not cancellable, so aborting the request does not
 * stop the work, it only hides the result. So these carry NO client abort at all.
 *
 * FAILURE IT PREVENTS: `POST /api/users/demo/activate` runs the exact same
 * `ImportProcessorService.processImport` as a statement upload — and the upload issues it
 * through `apps/web/src/core/api/download-client.ts#uploadPost`, a raw multipart fetch with no
 * timeout. Riding this spine, the identical work inherited the 30 s instance default instead, so
 * one operation had two verdicts: past 30 s ky aborts the fetch, the user is told "Failed to
 * activate demo mode", the server finishes the import and flags the account a demo user anyway,
 * and the next click answers 409 "already in demo mode". It surfaced as an e2e flake
 * (2026-09-15, run 35006351003): an aborted request emits NO `response` event, so the spec's
 * `waitForResponse` sat out its whole budget waiting for an answer the client had already
 * refused to receive. Detected here rather than at the call site so no caller can forget it;
 * the population is pinned by `ky-runtime.spec.ts` so a second importer-backed path lands here.
 */
export function isStatementImportEndpoint(
  endpoint: Pick<ZodiosEndpointDefinition, 'method' | 'path'>,
): boolean {
  return MUTATION_METHODS.has(endpoint.method) && endpoint.path === '/api/users/demo/activate';
}

/**
 * The timeout the RUNTIME imposes on an endpoint: `false` = never abort, a number = that budget,
 * `undefined` = leave ky's 30 s instance default. A call-site `config.timeout` still wins.
 */
function runtimeTimeout(
  endpoint: Pick<ZodiosEndpointDefinition, 'method' | 'path'>,
): number | false | undefined {
  if (isStatementImportEndpoint(endpoint)) return false;
  if (isLlmEndpoint(endpoint)) return LLM_REQUEST_TIMEOUT_MS;
  return undefined;
}

function interpolatePath(path: string, params: Record<string, unknown> | undefined): string {
  return path.replace(/:([A-Za-z_][A-Za-z0-9_]*)/g, (_, name: string) => {
    const value = params?.[name];
    if (value === undefined || value === null) {
      throw new Error(`Missing path param '${name}' for '${path}'`);
    }
    return encodeURIComponent(String(value));
  });
}

function buildSearchParams(queries: Record<string, unknown> | undefined): URLSearchParams | null {
  if (!queries) {
    return null;
  }
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(queries)) {
    if (value === undefined || value === null) {
      continue;
    }
    if (Array.isArray(value)) {
      for (const item of value) {
        searchParams.append(key, String(item));
      }
    } else {
      searchParams.append(key, String(value));
    }
  }
  return [...searchParams.keys()].length > 0 ? searchParams : null;
}

function buildFormData(body: unknown): FormData {
  const formData = new FormData();
  if (body && typeof body === 'object') {
    for (const [key, value] of Object.entries(body)) {
      if (value === undefined || value === null) {
        continue;
      }
      formData.append(key, value instanceof Blob ? value : String(value));
    }
  }
  return formData;
}

async function parseAndValidate(
  endpoint: ZodiosEndpointDefinition,
  response: Response,
): Promise<unknown> {
  // No-content responses carry nothing to parse: a 204/205, or a controller that
  // returns `null` (NestJS answers 200 with an EMPTY body and no content-type).
  // These MUST resolve to `undefined` — never an empty Blob. A size-0 Blob is a
  // truthy object that silently poisons every nullable (`X | null`) read: it
  // suppressed portfolio-insight auto-generation and crashed the card on
  // `insight.dayChangePercent.toFixed()`. Callers map undefined/null per their own
  // contract (e.g. a TanStack queryFn coerces to `null`).
  if (response.status === 204 || response.status === 205) return undefined;

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('json')) {
    // csv/xml/pdf/image endpoints — validation applies to JSON only (Zodios parity)
    if (contentType.startsWith('text/') || contentType.includes('xml')) {
      return response.text();
    }
    const blob = await response.blob();
    return blob.size === 0 ? undefined : blob;
  }

  const text = await response.text();
  const data: unknown = text ? JSON.parse(text) : undefined;
  const result = endpoint.response.safeParse(data);
  if (!result.success) {
    throw new StoxlyValidationError(`${endpoint.method} ${endpoint.path}`, data, result.error);
  }
  return result.data;
}

export function createKyAliasClient(
  endpoints: readonly ZodiosEndpointDefinition[],
  options: KyRuntimeOptions,
): Record<string, (...args: unknown[]) => Promise<unknown>> {
  const { baseUrl, getToken, refresh, kyOptions } = options;
  const kyInstance = ky.create({
    timeout: 30_000,
    // transport retries off by default: retry policy belongs to the caller
    // (TanStack Query); the 401 refresh retry below is runtime-level
    retry: 0,
    ...kyOptions,
  });

  let refreshInFlight: Promise<string | null> | null = null;
  const singleFlightRefresh = (): Promise<string | null> => {
    refreshInFlight ??= refresh!
      .refreshToken()
      .catch(() => null)
      .finally(() => {
        refreshInFlight = null;
      });
    return refreshInFlight;
  };

  async function dispatch(
    endpoint: ZodiosEndpointDefinition,
    config: RequestConfig,
    body: unknown,
    isRetry: boolean,
  ): Promise<unknown> {
    // Append the query string to the URL directly rather than via ky's
    // `searchParams` option: React Native's `react-native-url-polyfill`
    // `URLSearchParams` is not the instance ky's option handling expects, so ky
    // silently DROPS it and the request goes out with no query string (the
    // backend then falls back to its defaults — e.g. analytics `timeframe`
    // reverting to 1Y). A pre-serialized query string works identically on web
    // and native.
    const queryString = buildSearchParams(config.queries)?.toString();
    const url =
      baseUrl +
      interpolatePath(endpoint.path, config.params) +
      (queryString ? `?${queryString}` : '');
    const headers = new Headers(config.headers);
    const token = await getToken?.();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const timeout = config.timeout ?? runtimeTimeout(endpoint);
    const requestOptions: KyOptions = {
      method: endpoint.method,
      headers,
      ...(config.signal ? { signal: config.signal } : {}),
      ...(timeout !== undefined ? { timeout } : {}),
    };
    if (body !== undefined) {
      if (endpoint.requestFormat === 'form-data') {
        requestOptions.body = buildFormData(body);
      } else {
        requestOptions.json = body;
      }
    }

    try {
      const response = await kyInstance(url, requestOptions);
      return await parseAndValidate(endpoint, response);
    } catch (error) {
      if (isHTTPError(error)) {
        const { response } = error;
        if (response.status === 401 && !isRetry && refresh) {
          const newToken = await singleFlightRefresh();
          if (newToken) {
            return dispatch(endpoint, config, body, true);
          }
          await refresh.onSessionExpired?.();
        }
        // ky v2 pre-parses the body into error.data (the response body is consumed)
        throw new StoxlyApiError(response.status, error.data, response.headers.get('retry-after'));
      }
      throw error;
    }
  }

  const client: Record<string, (...args: unknown[]) => Promise<unknown>> = {};
  for (const endpoint of endpoints) {
    if (!endpoint.alias) {
      continue;
    }
    client[endpoint.alias] = MUTATION_METHODS.has(endpoint.method)
      ? (body?: unknown, config?: unknown) =>
          dispatch(endpoint, (config ?? {}) as RequestConfig, body, false)
      : (config?: unknown) => dispatch(endpoint, (config ?? {}) as RequestConfig, undefined, false);
  }
  return client;
}
